/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {defineMessage, defineMessages} from 'react-intl'
import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS,
} from '@salesforce/retail-react-app/app/constants'

DEFAULT_LIMIT_VALUES[0] = 16
DEFAULT_SEARCH_PARAMS.limit = 16

const ACTIVE_DATA_ENABLED = false

export const overridenMessagesPlural = defineMessages({
    orderSummaryCartItemSingle: {
        id: 'order_summary.cart_items.action.num_of_items_in_cart',
        description: 'Message for a single item in the cart',
        defaultMessage: '{itemCount} item in cart',
    },
    orderSummaryCartItemsPlural: {
        id: 'order_summary.cart_items.action.num_of_items_in_cart_plural',
        description: 'Message for multiple items in the cart',
        defaultMessage: '{itemCount} items in cart',
    },
})

// Ensure totalItems is defined or passed as a parameter where this message is used

export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS, ACTIVE_DATA_ENABLED}

export const TOAST_MESSAGE_ADDED_TO_WISHLIST = defineMessage({
    id: 'global.info.added_to_wishlist',
    defaultMessage: 'Added to favorites',
})

export const TOAST_MESSAGE_ADDED_TO_WISHLIST_DESCRIPTION = defineMessage({
    defaultMessage:
        'Warning: your wishlist is temporary! Complete your purchase or log in to save your favorite items before they expire',
    id: 'global.info.added_to_favorites_description',
})

export const TOAST_MESSAGE_GO_TO_WISHLIST = defineMessage({
    id: 'wishlist.temporary.to-wishlist',
    defaultMessage: 'Go to wishlist',
})

export const API_ERROR_MESSAGE = defineMessage({
    id: 'global.error.something_went_wrong',
    defaultMessage: 'Something went wrong. Try again!',
})

export const ADD_TO_CART_ERROR_MESSAGES = {
    NO_VARIANT_SELECTED: defineMessage({
        defaultMessage: 'Please select all your options above',
        id: 'general.message.select_your_options',
    }),
    INVENTORY_REMAINING_FOR_PRODUCT: defineMessage({
        defaultMessage: 'Only {stockLevel} left for {productName}!',
        id: 'general.message.inventory_remaining_for_product',
    }),
    OUT_OF_STOCK_FOR_PRODUCT: defineMessage({
        defaultMessage: 'Out of stock for {productName}',
        id: 'general.message.out_of_stock_for_product',
    }),
}

export const ADD_TO_CART_ERROR_KEYS = {
    NO_VARIANT_SELECTED: 1,
    INVENTORY_REMAINING_FOR_PRODUCT: 2,
    OUT_OF_STOCK_FOR_PRODUCT: 3,
}

export const MOCKED_RECOMMENDED = 'EC9155.YE,EN640.B,ECAM320.70.TB,ECAM322.70.SB,ECAM320.70.TB'
export const MOCKED_RECOMMENDED_ARRAY = [
    'EC9155.YE',
    'EN640.B',
    'ECAM320.70.TB',
    'ECAM322.70.SB',
    'ECAM320.70.TB',
]
export const PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID = 'subModelIdentifier'
export const PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID = 'skuIdentifier'

// Constants for shopper context
// Supported non-string field types used in SHOPPER_CONTEXT_SEARCH_PARAMS below.
// Only non-string types need to be identified using the "type" field.
// If no "type" field is present, the value will be parsed as a string by default.
export const SHOPPER_CONTEXT_FIELD_TYPES = {
    INT: 'int',
    DOUBLE: 'double',
    ARRAY: 'array',
}
export const SHOPPER_CONTEXT_SEARCH_PARAMS = {
    sourceCode: {paramName: 'cluid'},
    geoLocation: {
        city: {paramName: 'city'},
        country: {paramName: 'country'},
        countryCode: {paramName: 'countryCode'},
        latitude: {paramName: 'latitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        longitude: {paramName: 'longitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        metroCode: {paramName: 'metroCode'},
        postalCode: {paramName: 'postalCode'},
        region: {paramName: 'region'},
        regionCode: {paramName: 'regionCode'},
    },
    customQualifiers: {
        countryCode: {paramName: 'countryCode'},
    },
    assignmentQualifiers: {
        // Add assignment qualifiers here
    },
}

export const METRIC7_MULTIPLIER = 24.04

export const MAX_CACHE_AGE_PLP = 60 * 60 * 2 // 2 hours in seconds
export const STALE_WHILE_REVALIDATE_PLP = 60 * 60 * 2 // 2 hours in seconds

export const MAX_CACHE_AGE_PDP = 60 * 30 // 30 minutes in seconds
export const STALE_WHILE_REVALIDATE_PDP = 60 * 30 // 30 minutes in seconds

export * from '@salesforce/retail-react-app/app/constants'
